import type { LocaleId } from '../locales';
import type { TemplateDoc } from '../template';
import { VideoBuilder } from './kit';
import { TEMPLATES_A } from './templates-a';
import { TEMPLATES_B } from './templates-b';
import { TEMPLATES_C } from './templates-c';
import { TEMPLATES_D } from './templates-d';
import { TEMPLATES_REEL } from './templates-reel';
import { applyAutoSfx } from '../motion/auto-sfx';
import {
  VIDEO_GALLERY_CATEGORIES,
  type VideoGalleryCategory,
  type VideoGalleryEntry,
  type VideoTemplateDef,
} from './types';

/**
 * Video gallery: ready-made ad videos (TikTok/Reels/Shorts style) built scene by scene for any
 * format and language, each with its own editing technique, palette and motion vocabulary.
 */

export { VIDEO_GALLERY_CATEGORIES };
export type { VideoGalleryCategory, VideoGalleryEntry };

const DEFS: VideoTemplateDef[] = [
  ...TEMPLATES_REEL,
  ...TEMPLATES_A,
  ...TEMPLATES_B,
  ...TEMPLATES_C,
  ...TEMPLATES_D,
];
const DEF_MAP = new Map(DEFS.map((d) => [d.id, d]));

export const VIDEO_GALLERY: VideoGalleryEntry[] = DEFS.map(
  ({ id, category, name, description, format }) => ({ id, category, name, description, format }),
);

export function findVideoGalleryEntry(id: string): VideoGalleryEntry | undefined {
  return VIDEO_GALLERY.find((e) => e.id === id);
}

/** Build the template document in a format and language. */
export function buildVideoGalleryDoc(
  entry: VideoGalleryEntry | string,
  opts: { width: number; height: number; locale: LocaleId },
): TemplateDoc {
  const id = typeof entry === 'string' ? entry : entry.id;
  const def = DEF_MAP.get(id);
  if (!def) throw new Error(`Unknown video gallery template "${id}"`);
  const b = new VideoBuilder(opts, def.scenes);
  const background = def.build(b);
  const doc = b.build(background);
  return def.sound ? applyAutoSfx(doc) : doc;
}

export * from './reel';
